import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

export default function IntegrationsSettings() {
  const [googleLinked, setGoogleLinked] = useState(false);
  const [oauthConflict, setOauthConflict] = useState(false);

  const refreshGoogleLinked = async () => {
    const { data } = await supabase.auth.getUser();
    const identities = (data?.user as any)?.identities || [];
    const linked = identities.some((i: any) => i.provider === "google");
    setGoogleLinked(linked);
  };

  useEffect(() => {
    refreshGoogleLinked();
    const onFocus = () => refreshGoogleLinked();
    window.addEventListener("focus", onFocus);
    const { data } = supabase.auth.onAuthStateChange(async () => {
      await refreshGoogleLinked();
    });
    return () => {
      window.removeEventListener("focus", onFocus);
      try { (data as any)?.subscription?.unsubscribe?.(); } catch {}
    };
  }, []);

  const startGoogleOAuth = async () => {
    const res = await supabase.auth.linkIdentity({ provider: "google" as any } as any);
    if (!res.error) {
      await refreshGoogleLinked();
      return;
    }
    const code = (res.error as any)?.status || (res.error as any)?.code || "";
    const msg = (res.error as any)?.message || "";
    if (String(code).includes("manual_linking_disabled") || String(msg).includes("manual_linking_disabled")) {
      alert("Ative 'Manual linking' no provedor Google nas configurações do Supabase Auth para vincular sem trocar a sessão.");
      return;
    }
    if (String(code).includes("identity_already_exists") || String(msg).includes("identity_already_exists")) {
      setOauthConflict(true);
      return;
    }
    alert("Falha ao integrar Google (linkIdentity)");
  };

  const unlinkGoogleIdentity = async () => {
    const { error } = await (supabase.auth as any).unlinkIdentity({ provider: "google" });
    if (!error) {
      await refreshGoogleLinked();
    }
  };

  const continueWithGoogleOAuth = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google" as any,
      options: { redirectTo: window.location.origin, queryParams: { access_type: "offline", prompt: "consent" }, skipBrowserRedirect: true },
    } as any);
    if (!error && data?.url) {
      window.open(data.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="space-y-6" data-cy="integrations-settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Integrações</h1>
        <p className="text-muted-foreground">Gerencie integrações conectadas ao sistema</p>
      </div>

      <Tabs defaultValue="google" className="space-y-4">
        <TabsList>
          <TabsTrigger value="google">Google Drive</TabsTrigger>
          <TabsTrigger value="chatwoot">Chatwoot</TabsTrigger>
        </TabsList>

        <TabsContent value="google">
          <Card className="pt-5">
            <CardHeader>
              <CardTitle>Google Drive</CardTitle>
              <CardDescription>Conecte sua conta Google para indexar arquivos do Drive.</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-3">
              <div className="text-sm">
                {googleLinked ? "Integração ativa" : "Integração não ativa"}
              </div>
              <div className="flex gap-2">
                <Button onClick={startGoogleOAuth} disabled={googleLinked}>Integrar</Button>
                {googleLinked && (
                  <Button variant="outline" onClick={unlinkGoogleIdentity}>Desvincular</Button>
                )}
              </div>
            </CardContent>
          </Card>
          {oauthConflict && (
            <div className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Conta Google já vinculada</CardTitle>
                  <CardDescription>
                    Esta conta Google já está vinculada a outro usuário. Você pode entrar com Google nesta sessão (a sessão atual será trocada), ou cancelar e manter a sessão atual.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setOauthConflict(false)}>Cancelar</Button>
                  <Button onClick={continueWithGoogleOAuth}>Entrar com Google</Button>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="chatwoot">
          <Card className="pt-5">
            <CardContent className="grid gap-4 md:grid-cols-2">
              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle>Chatwoot</CardTitle>
                  <CardDescription>Configure e monitore a sincronização de contatos com o Chatwoot.</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button asChild variant="secondary" className="flex-1">
                      <Link to="/chatwoot">Configurar Chatwoot</Link>
                    </Button>
                    <Button asChild variant="outline" className="flex-1">
                      <Link to="/chatwoot/auditoria">Abrir auditoria</Link>
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">A auditoria exibe diffs completos dos dados sincronizados. A tela de configuração permite conectar, desconectar e atualizar o token de usuário.</p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

